import {
  DocumentationSelectors,
  DocumentationThunks,
} from 'modules/documentationPage/redux';
import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { DocumentCategory, DocumentDetails } from 'modules/types/Document';
import { InputWithLabel, SelectWithLabel } from 'modules/ui';
import { FormPane, FormPaneContainer } from 'modules/ui/form';
import { useForm } from 'modules/ui/form/useForm';
import { DatePicker } from 'modules/ui/input';
import { MutableRefObject, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationType } from 'types/ValidationType';
import { DateWrapper } from '../styled';

export interface UploadFormProps {
  documentCategory?: DocumentCategory;
  uploadedFilesExists?: boolean;
  uploadDocument?: (
    documentCategory: DocumentCategory,
    documentDetails: DocumentDetails,
  ) => void;
  formRef: MutableRefObject<HTMLFormElement | undefined>;
}

export const DocumentDetailsForm: React.FC<UploadFormProps> = ({
  documentCategory,
  uploadedFilesExists,
  uploadDocument,
  formRef,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [documentDetails, setDocumentDetails] = useState<DocumentDetails>({});

  const [validate, setValidate] = useState<boolean>(false);
  const { validateForm } = useForm();

  const documentTypeCodeOptions = useAppSelector(
    DocumentationSelectors.selectDocumentTypeCodesOptions,
  );

  useEffect(() => {
    if (!documentTypeCodeOptions) {
      dispatch(DocumentationThunks.fetchDocumentTypes());
    }
  }, [dispatch, documentTypeCodeOptions]);

  const handleDocumentFormSubmit = (event: any) => {
    event.preventDefault();
    setValidate(true);
    const validateErrors = validateForm(event);

    if (!validateErrors && uploadedFilesExists && documentCategory) {
      uploadDocument && uploadDocument(documentCategory, documentDetails);
    }
  };

  return (
    <FormPaneContainer
      noValidate
      onSubmit={handleDocumentFormSubmit}
      ref={formRef as RefObject<HTMLFormElement>}>
      <FormPane>
        <SelectWithLabel
          required
          searchable={false}
          options={documentTypeCodeOptions || []}
          label={t('documentationPage.documentType')}
          placeholderText={t('documentationPage.documentTypePlaceholder')}
          defaultValue={documentDetails?.type}
          onChange={(value, option) =>
            setDocumentDetails((prevState) => ({
              ...prevState,
              type: option?.label?.toString(),
            }))
          }
          validators={[ValidationType.REQUIRED]}
          validate={validate}
        />
        <InputWithLabel
          required
          label={t('documentationPage.documentName')}
          placeholderText={t('documentationPage.documentNamePlaceholder')}
          defaultValue={documentDetails?.name}
          onChange={(e) =>
            setDocumentDetails((prevState) => ({
              ...prevState,
              name: e.target.value,
            }))
          }
          validators={[ValidationType.REQUIRED]}
          validate={validate}
        />
        <DateWrapper>
          <DatePicker
            required
            label={t('documentationPage.documentIssueDate')}
            defaultValue={documentDetails?.issueDate}
            onChange={(value) =>
              setDocumentDetails((prevState) => ({
                ...prevState,
                issueDate: value,
              }))
            }
            validators={[ValidationType.REQUIRED]}
            validate={validate}
          />
        </DateWrapper>
      </FormPane>
    </FormPaneContainer>
  );
};
